/** Material Catalog — v1 expenses-calendar-materials.js, read-only Phase A.
 * Rows come from rec_materials / rec_material_categories (PHASE14 read
 * policies): `data` holds v1's full material object {name, website, price,
 * unit, supplier, vendor, categoryId, sku, internet, notes, thumbnail,
 * priceTrend, history[]}. Filter/search/card rules are v1's verbatim.
 * WRITES (add/edit/delete/import/Update Pricing) stay in v1 until Phase B. */
import { http } from "../../lib/http";
import { idbGet, idbSet } from "../../lib/idb";
import { SUPABASE_URL, SUPABASE_ANON_KEY } from "../../config";
import { backendFetch } from "../../lib/backend";
import { pageAll } from "../edits/api";

export interface MaterialCat {
  id: string; // v1 category id (c_tile…) — data.id, else nat_key
  name: string;
}

export interface CatalogMaterial {
  natKey: string;
  name: string;
  categoryId: string | null;
  price: number | null;
  unit: string | null;
  supplier: string | null; // brand/manufacturer
  vendor: string | null; // source-site id (homedepot…)
  sku: string | null;
  internet: string | null;
  notes: string | null;
  thumbnail: string | null;
  website: string | null;
  priceTrend: "lower" | "higher" | null;
  history: { date: string; price: number }[];
}

/** v1 MAT_VENDORS, verbatim. */
export const MAT_VENDORS: [string, string][] = [
  ["homedepot", "Home Depot"], ["lowes", "Lowe's"], ["amazon", "Amazon"],
  ["walmart", "Walmart"], ["target", "Target"], ["costco", "Costco"], ["other", "Other"],
];
export const vendorName = (v: string | null): string => MAT_VENDORS.find((x) => x[0] === v)?.[1] ?? (v ?? "");

/** v1 MAT_UNITS, verbatim — the Unit select options. */
export const MAT_UNITS = ["each", "sqft", "lf", "box", "gallon", "sheet", "roll", "bag", "bundle"];

/** Shape returned by the backend's /api/material-price scraper (v1 matImportUrl). */
export interface MaterialPriceResult {
  title?: string;
  price?: number;
  image?: string;
  source?: string;
  detail?: string;
}

/** v1 matImportUrl: GET /api/material-price?url=… → { title, price, image, source }.
 * Parsing is server-side (Amazon / Home Depot / Lowe's); we just consume the JSON. */
export async function lookupMaterialPrice(
  token: string | null,
  url: string,
  opts: { onWake?: () => void; signal?: AbortSignal } = {},
): Promise<MaterialPriceResult> {
  const q = encodeURIComponent(url.trim());
  return backendFetch<MaterialPriceResult>(token, "/api/material-price?url=" + q, {
    // bot-walled retailers (Home Depot) retry on fresh proxy IPs server-side
    // — a successful lookup can take 90s+; 45s made even the wins look dead
    timeoutMs: 150000,
    ...(opts.onWake ? { onWake: opts.onWake } : {}),
    ...(opts.signal ? { signal: opts.signal } : {}),
  });
}

const s = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v.trim() : null);
const n = (v: unknown): number | null => {
  const f = typeof v === "number" ? v : parseFloat(String(v ?? "").replace(/[^0-9.]/g, ""));
  return Number.isFinite(f) && f > 0 ? f : null;
};

export function narrowMaterial(row: Record<string, unknown>): CatalogMaterial | null {
  const nk = s(row["nat_key"]);
  if (!nk) return null;
  const d = (row["data"] ?? {}) as Record<string, unknown>;
  const name = s(d["name"]) ?? s(row["name"]);
  if (!name) return null;
  const trend = d["priceTrend"];
  const hist: { date: string; price: number }[] = [];
  if (Array.isArray(d["history"])) {
    for (const h of d["history"].slice(-40)) {
      if (h === null || typeof h !== "object") continue;
      const o = h as Record<string, unknown>;
      const p = n(o["price"]);
      if (p !== null) hist.push({ date: s(o["date"]) ?? "", price: p });
    }
  }
  return {
    natKey: nk,
    name,
    categoryId: s(d["categoryId"]) ?? s(row["category_id"]),
    price: n(d["price"]) ?? n(row["price"]),
    unit: s(d["unit"]),
    supplier: s(d["supplier"]),
    vendor: s(d["vendor"]),
    sku: s(d["sku"]),
    internet: s(d["internet"]),
    notes: s(d["notes"]),
    thumbnail: s(d["thumbnail"]),
    website: s(d["website"]),
    priceTrend: trend === "lower" ? "lower" : trend === "higher" ? "higher" : null,
    history: hist,
  };
}

export function narrowMaterialCat(row: Record<string, unknown>): MaterialCat | null {
  const d = (row["data"] ?? {}) as Record<string, unknown>;
  const id = s(d["id"]) ?? s(row["nat_key"]);
  const name = s(d["name"]) ?? s(row["name"]);
  if (!id || !name) return null;
  return { id, name };
}

/** v1 matCatName. */
export const catName = (cats: MaterialCat[], id: string | null): string =>
  cats.find((c) => c.id === id)?.name ?? "Uncategorized";

/** v1 getMaterials filter: category exact + every search word must appear in
 * name/supplier/sku/internet/notes/category-name. */
export function matFilter(rows: CatalogMaterial[], cats: MaterialCat[], cat: string, q: string): CatalogMaterial[] {
  let r = rows;
  if (cat !== "all") r = r.filter((m) => m.categoryId === cat);
  const words = q.toLowerCase().split(" ").filter(Boolean);
  if (words.length) {
    r = r.filter((m) => {
      const t = [m.name, m.supplier, m.sku, m.internet, m.notes, catName(cats, m.categoryId)]
        .filter(Boolean).join(" ").toLowerCase();
      return words.every((w) => t.includes(w));
    });
  }
  return r;
}

/** v1 card price format: $1,234.56 with 2 decimals. */
export const matPrice = (p: number): string =>
  "$" + p.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CACHE = "materials_v1";

export interface MaterialsResult {
  rows: CatalogMaterial[];
  cats: MaterialCat[];
  ready: boolean; // false = PHASE14 read policies not applied yet
  /** Set when the READ ITSELF failed (network, timeout, 5xx). Distinct from
   * ready:false — that is a database-policy state, this is "we don't know".
   * They used to be the same `ready:false`, so a dropped connection rendered
   * "0 materials" plus a pill blaming an unapplied PHASE14 migration. */
  error?: string;
}

export async function cachedMaterials(): Promise<MaterialsResult | null> {
  const c = await idbGet<MaterialsResult>(CACHE);
  return c && Array.isArray(c.rows) ? c : null;
}

export async function fetchMaterials(token: string, signal?: AbortSignal): Promise<MaterialsResult> {
  const headers = { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${token}` };
  let matRows: unknown[];
  let catRows: unknown[];
  try {
    // materials paged (PostgREST clamps limit to 1000 — a single shot silently
    // truncates); categories stay one small read — 200 would already be odd.
    [matRows, catRows] = await Promise.all([
      pageAll<unknown>(token, "rec_materials?select=nat_key,name,category_id,price,data&deleted_at=is.null&order=updated_at.desc", signal),
      http<unknown[]>(`${SUPABASE_URL}/rest/v1/rec_material_categories?select=nat_key,name,data&deleted_at=is.null&limit=200`, { headers, signal, timeoutMs: 20000 }),
    ]);
  } catch (e) {
    // ready stays TRUE: we have no evidence the migration is missing, and
    // claiming it is sends you to the database over what is usually a timeout.
    return { rows: [], cats: [], ready: true,
             error: e instanceof Error ? e.message : "the backend did not answer" };
  }
  const rows = (Array.isArray(matRows) ? matRows : [])
    .map((r) => narrowMaterial(r as Record<string, unknown>))
    .filter((m): m is CatalogMaterial => m !== null);
  const cats = (Array.isArray(catRows) ? catRows : [])
    .map((r) => narrowMaterialCat(r as Record<string, unknown>))
    .filter((c): c is MaterialCat => c !== null);
  const out = { rows, cats, ready: true };
  await idbSet(CACHE, out);
  return out;
}

export const MAT_MSG = {
  title: "Material Catalog",
  sub: "Manage your materials database",
  phaseB: "Add / edit / Import CSV / Update Pricing stay in v1 until the live-fire session — this catalog is read-only here.",
  empty: "No materials found",
  notReady: "The materials read policies (PHASE14) haven't been applied to the database yet.",
  noImage: "No image",
  imgFail: "Image unavailable",
  readOnly: "Editing is read-only here until MIGRATION_CRM2_PHASE24_MATERIALS_WRITE.sql is run (and its selftest passes).",
};

// ---- Phase 24: materials editing (v1 saveMaterial/delMaterial/categories) ----
import type { RecOp } from "../../lib/outbox";
import { natKey } from "../../lib/natkey";

/** The editable material fields (v1 saveMaterial rec). */
export interface MaterialInput {
  name: string;
  categoryId: string | null;
  price: number;
  unit: string;
  supplier: string;
  vendor: string;
  sku: string;
  internet: string;
  notes: string;
  website: string;
  thumbnail: string;
}

let _mid = 0;
export const matId = (): string => "m" + Date.now().toString(36) + (_mid++).toString(36);
let _cid = 0;
export const matCatId = (): string => "c_" + Date.now().toString(36) + (_cid++).toString(36);

export const emptyMaterial = (categoryId: string | null = null): MaterialInput =>
  ({ name: "", categoryId, price: 0, unit: "each", supplier: "", vendor: "", sku: "", internet: "", notes: "", website: "", thumbnail: "" });

/** v1 _extUrl: a bare "amazon.com/x" gets an https:// scheme so links work. */
export function extUrl(u: string): string {
  const t = u.trim();
  if (!t) return "";
  return /^https?:\/\//i.test(t) ? t : "https://" + t;
}

/** Upsert a material through the door (data blob carries v1's full record). The
 * existing history is preserved by the caller passing it in `keepHistory`. */
export function opPutMaterial(id: string, m: MaterialInput, keepHistory: { date: string; price: number }[] = []): RecOp {
  const data: Record<string, unknown> = {
    id, name: m.name.trim(), categoryId: m.categoryId, price: m.price, unit: m.unit || "each",
    supplier: m.supplier, vendor: m.vendor, sku: m.sku, internet: m.internet, notes: m.notes,
    website: extUrl(m.website), thumbnail: m.thumbnail, history: keepHistory,
  };
  return { s: "materials", op: "put", k: natKey(id), d: data };
}
export function opDelMaterial(natKeyOrId: string): RecOp {
  return { s: "materials", op: "del", k: natKey(natKeyOrId) };
}
export function opPutMatCat(id: string, name: string): RecOp {
  return { s: "material_categories", op: "put", k: natKey(id), d: { id, name: name.trim() } };
}
export function opDelMatCat(id: string): RecOp {
  return { s: "material_categories", op: "del", k: natKey(id) };
}

const PROBE_KEY = "materials_caps_v1";

/** Can this database accept materials writes yet? Probed with a no-op `del` on a
 * synthetic key (same technique as deals). 200 = PHASE24 ran and the core routes
 * the section; any error = keep the catalog read-only. Cached per session. */
export async function probeMaterialsWrite(token: string): Promise<boolean> {
  const cached = await idbGet<{ at: number; open: boolean }>(PROBE_KEY);
  if (cached && cached.open) return true; // once open, always open
  try {
    await http(`${SUPABASE_URL}/rest/v1/rpc/crm2_apply_ops`, {
      method: "POST",
      headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${token}` },
      body: { ops: [{ s: "materials", op: "del", k: "__crm2_materials_capability_probe__" }] },
      attempts: 1,
      timeoutMs: 12000,
    });
    await idbSet(PROBE_KEY, { at: Date.now(), open: true });
    return true;
  } catch {
    await idbSet(PROBE_KEY, { at: Date.now(), open: false });
    return false;
  }
}

/** v1 doMatCsvImport parser: header row must include name + category; each row's
 * category must match an existing category name (case-insensitive). Pure so the
 * mapping is unit-tested; the page enqueues one put op per parsed material. */
/** Split one CSV line, honouring double-quoted fields (`"1,250.00"`) and the
 * `""` escape. A plain `.split(",")` tore quoted numbers in half AND shifted
 * every column after them, so one thousands-separated price silently corrupted
 * the whole rest of the row. */
export function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = "";
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (quoted) {
      if (c === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++; } else { quoted = false; }
      } else cur += c;
    } else if (c === '"') {
      quoted = true;
    } else if (c === ",") {
      out.push(cur); cur = "";
    } else cur += c;
  }
  out.push(cur);
  return out.map((v) => v.trim());
}

export function parseMatCsv(text: string, cats: MaterialCat[]): { materials: { id: string; input: MaterialInput }[]; errors: string[] } {
  const errors: string[] = [];
  const materials: { id: string; input: MaterialInput }[] = [];
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) { errors.push("CSV is empty or has no data rows"); return { materials, errors }; }
  const header = splitCsvLine(lines[0] ?? "").map((h) => h.toLowerCase());
  if (!header.includes("name") || !header.includes("category")) { errors.push("Missing required columns: name, category"); return { materials, errors }; }
  const catByName = new Map(cats.map((c) => [c.name.toLowerCase(), c]));
  for (let i = 1; i < lines.length; i++) {
    const vals = splitCsvLine(lines[i] ?? "");
    const row: Record<string, string> = {};
    header.forEach((h, idx) => { row[h] = vals[idx] ?? ""; });
    if (!row["name"]) { errors.push(`Row ${i + 1}: missing name`); continue; }
    const cat = catByName.get(String(row["category"] ?? "").toLowerCase());
    if (!cat) { errors.push(`Row ${i + 1} (${row["name"]}): category "${row["category"] ?? ""}" not found`); continue; }
    // n() strips $ and , the way every other money field in this file does.
    // parseFloat("$3.49") is NaN and parseFloat("1,250.00") is 1 — both used to
    // land as 0.00 with NO error reported, so a supplier catalog priced the
    // normal way imported as free and quietly deflated every SOW built on it.
    const priceRaw = row["price"];
    const price = priceRaw ? (n(priceRaw) ?? 0) : 0;
    if (priceRaw && price === 0) errors.push(`Row ${i + 1} (${row["name"]}): could not read a price from "${priceRaw}"`);
    materials.push({
      id: matId(),
      input: {
        name: row["name"] ?? "", categoryId: cat.id, price, unit: row["unit"] || "each",
        supplier: row["supplier"] ?? "", vendor: row["vendor"] ?? "", sku: row["sku"] ?? "",
        internet: row["internet_number"] ?? row["internet #"] ?? "", notes: row["notes"] ?? row["description"] ?? "",
        website: row["website_url"] ?? row["url"] ?? "", thumbnail: row["thumbnail_url"] ?? row["image_url"] ?? "",
      },
    });
  }
  return { materials, errors };
}
